import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckSquare, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/personal", { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-[100dvh] flex auth-bg grain-bg" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-border relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <CheckSquare size={20} className="text-primary-foreground" />
          </div>
          <span className="font-display font-extrabold text-lg">FlowTasks<span className="text-primary">360</span></span>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">A productivity OS</div>
          <h1 className="font-display text-5xl font-black tracking-tight leading-[0.95] mb-5">
            One workspace.<br/>
            <span className="text-primary">Every device.</span><br/>
            Total clarity.
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Personal, Family and Work — all your tasks synced across desktop, iPhone and iPad. Log in
            once and pick up exactly where you left off.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
            {["Kanban boards", "Recurring tasks", "Cross-device sync"].map((f) => (
              <div key={f} className="rounded-md border border-border bg-card/60 px-3 py-2.5">
                <span className="block w-1 h-1 rounded-full bg-primary mb-1.5" />
                {f}
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground relative z-10">© 2026 360DMA • Designed & Engineered by 360 Digital Marketing</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <CheckSquare size={20} className="text-primary-foreground" />
            </div>
            <span className="font-display font-extrabold text-lg">FlowTasks<span className="text-primary">360</span></span>
          </div>

          <h2 className="font-display text-3xl font-bold mb-2">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-7">
            Welcome back. Enter your credentials to access your dashboards.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <div className="relative mt-1.5">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-11"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 font-medium"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground"><span className="bg-background px-2">or</span></div>
            </div>
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/auth/callback";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="login-google-btn"
              className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card hover:bg-secondary/60 transition text-sm font-medium"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16 4 9 8.7 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2c-1.9 1.3-4.4 2.1-7.2 2.1-5.3 0-9.7-3.1-11.3-7.7l-6.5 5C9 39.3 16 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.5 5.9l6.2 5.2C40.7 35.3 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            New here?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
              Create an account
            </Link>
            {" · "}
            <Link to="/forgot-password" className="text-primary hover:underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
